import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { rawMaterialService } from "~/services/purchasing";
import type { RawMaterialFormData } from "~/types/purchasing";

const QUERY_KEY = "raw_materials";
const PRICES_QUERY_KEY = "raw_material_prices";

export function useRawMaterials() {
  return useQuery({
    queryKey: [QUERY_KEY],
    queryFn: () => rawMaterialService.getAll(),
  });
}

export function useRawMaterial(id: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => rawMaterialService.getById(id!),
    enabled: !!id,
  });
}

export function useLowStockRawMaterials() {
  return useQuery({
    queryKey: [QUERY_KEY, "low_stock"],
    queryFn: () => rawMaterialService.getLowStock(),
  });
}

export function useCreateRawMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RawMaterialFormData) => rawMaterialService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdateRawMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<RawMaterialFormData>;
    }) => rawMaterialService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useDeleteRawMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => rawMaterialService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

// Price hooks
export function useRawMaterialPriceHistory(rawMaterialId: string | undefined) {
  return useQuery({
    queryKey: [PRICES_QUERY_KEY, rawMaterialId],
    queryFn: () => rawMaterialService.getPriceHistory(rawMaterialId!),
    enabled: !!rawMaterialId,
  });
}

export function useLatestRawMaterialPrice(
  rawMaterialId: string | undefined,
  supplierId?: string
) {
  return useQuery({
    queryKey: [PRICES_QUERY_KEY, "latest", rawMaterialId, supplierId],
    queryFn: () =>
      rawMaterialService.getLatestPrice(rawMaterialId!, supplierId),
    enabled: !!rawMaterialId,
  });
}

export function useAddRawMaterialPrice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      raw_material_id: string;
      supplier_id?: string;
      unit_price: number;
      currency: "CLP" | "USD";
      effective_date: string;
    }) => rawMaterialService.addPrice(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PRICES_QUERY_KEY, variables.raw_material_id],
      });
      queryClient.invalidateQueries({
        queryKey: [PRICES_QUERY_KEY, "latest", variables.raw_material_id],
      });
    },
  });
}
